import React, { Suspense, lazy, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { MotionConfig } from 'framer-motion'
import { AuthProvider } from './contexts/AuthContext'
import { AIAssistantProvider } from './contexts/AIAssistantContext'
import { CelebrationProvider } from './components/Celebrations'
import { LanguageProvider } from './contexts/LanguageContext'
import { POSSessionProvider } from './contexts/POSSessionContext'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
// Utilities moved to Settings page
import ToastCleaner from './components/ToastCleaner'
import OfflineIndicator from './components/OfflineIndicator'
import { Toaster } from 'sonner'
import { Toaster as HotToaster } from 'react-hot-toast'
import { initSyncService } from './services/syncService'
import { initOfflineDB } from './services/offlineDB'
import './services/invoiceService'
import './services/itemService'
import './services/partyService'
import { PWAUpdateReady } from './components/PWAUpdateReady'
import BlockingOverlay from './components/BlockingOverlay'
import { useBlockingStore } from './hooks/useBlockingStore'
import { shouldEnableSubscriptionAdminControls } from './config/customerBuild'

const Login = lazy(() => import('./pages/Login'))
const Setup = lazy(() => import('./pages/Setup'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Sales = lazy(() => import('./pages/Sales'))
const Purchases = lazy(() => import('./pages/Purchases'))
const Parties = lazy(() => import('./pages/Parties'))
const Inventory = lazy(() => import('./pages/Inventory'))
const ReportsNew = lazy(() => import('./pages/ReportsNew'))
const Expenses = lazy(() => import('./pages/Expenses'))
const OnlineStore = lazy(() => import('./pages/OnlineStore'))
const Banking = lazy(() => import('./pages/Banking'))
const Settings = lazy(() => import('./pages/Settings'))
const Quotations = lazy(() => import('./pages/Quotations'))
const More = lazy(() => import('./pages/More'))
const PartyStatement = lazy(() => import('./pages/PartyStatement'))
const CreditNotes = lazy(() => import('./pages/CreditNotes'))
const Returns = lazy(() => import('./pages/Returns'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const CompanyInfo = lazy(() => import('./pages/CompanyInfo'))
const Profile = lazy(() => import('./pages/Profile'))
const CRMPage = lazy(() => import('./crm/pages/CRMPage'))
const SuperAdmin = lazy(() => import('./pages/SuperAdmin'))

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
    Loading...
  </div>
)

function App() {
  // SECURITY: Initial admin bootstrap should not be exposed in production.
  // Keep /setup available only in local development.
  const showSetupRoute = import.meta.env.DEV

  // Initialize offline services on app mount
  useEffect(() => {
    const initOfflineServices = async () => {
      try {
        // Initialize IndexedDB
        await initOfflineDB()
        console.log('✅ Offline database ready')

        // Initialize sync service
        initSyncService()
        console.log('✅ Sync service initialized')

      } catch (error) {
        console.error('Failed to initialize offline services:', error)
      }
    }

    initOfflineServices()
  }, [])

  // Prevent mouse wheel from changing number input values globally
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'number') {
        // Blur the input to prevent value change, or just prevent default
        target.blur()
      }
    }

    // Passive listener keeps scrolling smooth; we don't call preventDefault here.
    document.addEventListener('wheel', handleWheel, { passive: true })
    return () => document.removeEventListener('wheel', handleWheel)
  }, [])

  const blocking = useBlockingStore()
  return (
    <Router>
      <MotionConfig reducedMotion="always">
        <ThemeProvider>
          <LanguageProvider>
            <POSSessionProvider>
              <AuthProvider>
                <AIAssistantProvider>
                  <CelebrationProvider>
                    <div className="min-h-screen bg-background text-foreground overflow-x-hidden max-w-[100vw]">
                      <PWAUpdateReady />
                      <ToastCleaner />
                      <OfflineIndicator position="bottom-left" />
                      <BlockingOverlay
                        open={blocking.count > 0}
                        title={blocking.title || 'Please wait...'}
                        description={blocking.description || 'Working...'}
                      />
                      <Toaster
                        position="top-center"
                        richColors
                        closeButton
                        duration={3000}
                        visibleToasts={1}
                        toastOptions={{
                          className: 'font-medium',
                          style: {
                            background: 'white',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius)',
                          }
                        }}
                      />
                      <HotToaster
                        position="top-center"
                        toastOptions={{
                          className: 'font-medium',
                          style: {
                            background: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            padding: '16px',
                          },
                          success: {
                            iconTheme: {
                              primary: '#10b981',
                              secondary: 'white',
                            },
                          },
                        }}
                      />
                      <Suspense fallback={<RouteFallback />}>
                        <Routes>
                          <Route path="/login" element={<Login />} />
                          {showSetupRoute && <Route path="/setup" element={<Setup />} />}
                          <Route path="/payment-success" element={<PaymentSuccess />} />
                          <Route
                            path="/"
                            element={
                              <ProtectedRoute>
                                <Layout />
                              </ProtectedRoute>
                            }
                          >
                            {/* All routes use pageKey for permission checking (configured in Settings > Page Permissions) */}
                            <Route index element={<ProtectedRoute pageKey="dashboard"><Dashboard /></ProtectedRoute>} />
                            <Route path="sales" element={<ProtectedRoute pageKey="sales"><Sales /></ProtectedRoute>} />
                            <Route path="pos" element={<ProtectedRoute pageKey="pos"><Sales /></ProtectedRoute>} />
                            <Route path="quotations" element={<ProtectedRoute pageKey="quotations"><Quotations /></ProtectedRoute>} />

                            {/* These pages check page permissions (admin can grant access to any role) */}
                            <Route path="purchases" element={<ProtectedRoute pageKey="purchases"><Purchases /></ProtectedRoute>} />
                            <Route path="expenses" element={<ProtectedRoute pageKey="expenses"><Expenses /></ProtectedRoute>} />
                            <Route path="parties" element={<ProtectedRoute pageKey="parties"><Parties /></ProtectedRoute>} />
                            <Route path="party-statement" element={<ProtectedRoute pageKey="parties"><PartyStatement /></ProtectedRoute>} />
                            <Route path="credit-notes" element={<ProtectedRoute pageKey="sales"><CreditNotes /></ProtectedRoute>} />
                            <Route path="returns" element={<ProtectedRoute pageKey="sales"><Returns /></ProtectedRoute>} />
                            <Route path="inventory" element={<ProtectedRoute pageKey="inventory"><Inventory /></ProtectedRoute>} />
                            <Route path="reports" element={<ProtectedRoute pageKey="reports"><ReportsNew /></ProtectedRoute>} />
                            <Route path="banking" element={<ProtectedRoute pageKey="banking"><Banking /></ProtectedRoute>} />

                            {/* Utilities moved to Settings page */}

                            {/* Admin only - Settings */}
                            <Route path="settings" element={<ProtectedRoute pageKey="settings"><Settings /></ProtectedRoute>} />
                            <Route path="company-info" element={<ProtectedRoute pageKey="settings"><CompanyInfo /></ProtectedRoute>} />

                            {/* Other pages */}
                            <Route path="profile" element={<Profile />} />
                            <Route path="online-store" element={<OnlineStore />} />
                            <Route path="more" element={<ProtectedRoute pageKey="others"><More /></ProtectedRoute>} />

                            {/* CRM Module - supports nested routes like /crm/leads/:id */}
                            <Route path="crm/*" element={<ProtectedRoute pageKey="crm"><CRMPage /></ProtectedRoute>} />

                            {/* Super Admin - Hidden route for managing subscriptions */}
                            {shouldEnableSubscriptionAdminControls && (
                              <Route
                                path="super-admin"
                                element={
                                  <ProtectedRoute allowedRoles={['super_admin']}>
                                    <SuperAdmin />
                                  </ProtectedRoute>
                                }
                              />
                            )}
                          </Route>
                        </Routes>
                      </Suspense>
                    </div>
                  </CelebrationProvider>
                </AIAssistantProvider>
              </AuthProvider>
            </POSSessionProvider>
          </LanguageProvider>
        </ThemeProvider>
      </MotionConfig>
    </Router>
  )
}

export default App
